import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useInventoryApi } from '~Components/InventoryUniverse';
import QtyChanger from '~Components/QtyChanger';
import Button from '~Components/Button';
import { useI18n } from '~Hooks';

const Content = styled.div`
  padding: 40px 0;
  margin: 0 auto;
`;

const Text = styled.p`
  font-size: 20px;
  letter-spacing: 0.21px;
  line-height: 20px;
  margin: 0 0 4px;
  color: #41474f;
`;

const CommentWrapper = styled.div`
  animation: fade-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const MessageButtons = styled.div`
  display: flex;
  flex-flow: row wrap;
  margin: -4px;
  padding: 8px 48px 24px;
  justify-content: center;
`;

const MessageBtn = styled.div`
  background-color: #005d68;
  color: #fff;
  padding: 6px 12px;
  margin: 4px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.24px;
`;

const Comment = styled.textarea`
  width: 264px;
  height: 110px;
  border-radius: 8px;
  overflow: hidden;
  border: none;
  padding: 16px;
  box-sizing: border-box;
  outline: none;
  border: 1px solid #e8e9ea;
  font-family: 'Eina01', 'HelveticaNeue', Helvetica, Arial, sans-serif;

  &:focus {
    border-color: #005d68;
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  padding: 16px;
  margin-top: auto;
  background-color: #fff;

  div:nth-child(2) {
    flex: 1;
    margin-left: 16px;
  }
`;

const ErrorMsg = styled.p`
  color: #bd1414;
  margin: 8px 0 0 0;
  font-size: 14px;
`;

interface Props {
  venueId: string;
  itemId: string;
  defaultValue: number;
  isOutOfStock: boolean;
  goBack: () => void;
  handleClose: () => void;
}

const predefinedMessages = ['Retur', 'Okänt'];
const predefinedMessagesReduce = ['Svinn', 'Gåva', 'Utgånget', 'Retur', 'Okänt'];

const Override: React.FunctionComponent<Props> = ({
  venueId,
  itemId,
  defaultValue,
  isOutOfStock,
  goBack,
  handleClose,
}) => {
  const { i18n } = useI18n();
  const inventory = useInventoryApi(venueId);
  const [quantity, setQuantity] = useState<number>(defaultValue || 0);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState('');
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isOutOfStock && quantity !== defaultValue) {
      setShowComment(true);
    } else {
      setShowComment(false);
    }
  }, [quantity]);

  function handleInput(value: number) {
    setQuantity(value);
  }

  function handleSetCount(value: number) {
    const newCount = Math.max(0, quantity + value);
    setQuantity(newCount);
  }

  function saveValue() {
    setError(false);

    if (!isOutOfStock && quantity < defaultValue && comment.length < 1) {
      setError(true);
    } else if (!isOutOfStock && quantity < defaultValue) {
      inventory.reduceStock(itemId, defaultValue - quantity, comment);
      handleClose();
    } else {
      inventory.addStock(itemId, quantity - defaultValue);
      handleClose();
    }
  }

  const quantityVal = quantity ? quantity : 0;

  return (
    <>
      <Content>
        <Text>{i18n('Inventory.Balance')}</Text>
        <QtyChanger
          defaultValue={isOutOfStock ? 0 : quantityVal}
          handleInput={(val) => handleInput(val)}
          handleDecrease={() => handleSetCount(-1)}
          handleIncrease={() => handleSetCount(+1)}
        />
        {showComment && (
          <CommentWrapper>
            <MessageButtons>
              {(quantity < defaultValue ? predefinedMessagesReduce : predefinedMessages).map(
                (message, i) => (
                  <MessageBtn key={i} onTouchStart={() => setComment(message)}>
                    {message}
                  </MessageBtn>
                ),
              )}
            </MessageButtons>
            <Comment
              value={comment}
              placeholder={i18n('ReasonReduction')}
              onChange={(e) => setComment(e.target.value)}
            />
          </CommentWrapper>
        )}
        {error && <ErrorMsg>{i18n('Inventory.ReasonReductionMessage')}</ErrorMsg>}
      </Content>
      <ButtonsWrapper>
        <Button
          handleClick={goBack}
          text={i18n('Inventory.Back')}
          width="200px"
          textColor="#2f353d"
          background="transparent"
          border="1px solid #1c232c"
        />
        <Button text={i18n('Inventory.Save')} handleClick={saveValue} />
      </ButtonsWrapper>
    </>
  );
};

export default Override;
